import { useQuery } from '@tanstack/react-query';
import { makeApiCall } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useOrgFeatureFlags } from './useOrgFeatureFlags';

/** The server-side flag that gates the Help Center (`FEATURE_FLAG_DEFINITIONS`). */
export const HELP_CENTER_FLAG = 'help_center' as const;

export const PUBLIC_FEATURE_FLAGS_QUERY_KEY = ['public-feature-flags'] as const;

/**
 * `GET /feature-flags/public`: the platform-wide value of the flags public
 * pages need, readable without signing in. A failed read counts as off.
 */
export const fetchPublicFlags = async (): Promise<Record<string, boolean>> => {
  try {
    const response = await makeApiCall('/feature-flags/public');
    if (!response.ok) return {};
    const body = await response.json();
    return (body?.data?.flags as Record<string, boolean> | undefined) ?? {};
  } catch {
    return {};
  }
};

/**
 * Whether to show the Help Center and the links to it.
 *
 * Public pages cannot resolve an organization, so the global setting of
 * `help_center` (read through the public endpoint) is what turns it on for
 * everyone. A signed-in member of an organization with its own override sees
 * it too, which is how a pilot store gets it before the global switch.
 */
export const useHelpCenterEnabled = (): { enabled: boolean; isLoading: boolean } => {
  const { currentUser } = useAuth();
  const signedIn = Boolean(currentUser);
  const publicFlags = useQuery({
    queryKey: PUBLIC_FEATURE_FLAGS_QUERY_KEY,
    queryFn: fetchPublicFlags,
    staleTime: 60_000,
    retry: false,
  });
  const org = useOrgFeatureFlags({ enabled: signedIn });

  const enabled = publicFlags.data?.[HELP_CENTER_FLAG] === true || (signedIn && org.isEnabled(HELP_CENTER_FLAG));
  const isLoading = !enabled && (publicFlags.isLoading || (signedIn && org.isLoading));
  return { enabled, isLoading };
};
